import { z, ZodDate, ZodType } from 'zod';

export type ResponseBase =
  | Record<string, unknown>
  | unknown[]
  | number
  | string
  | null
  | boolean
  | undefined
  | void;

export const formErrorsSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  errors: z.record(z.string(), z.array(z.string())),
});

export const listResponseSchema = <TItemSchema extends z.ZodType>(itemSchema: TItemSchema) =>
  z.object({
    result: z.array(itemSchema),
    pageSize: z.number(),
    pageCount: z.number(),
    rowCount: z.number(),
  });

export type ListResponse<TItem> = {
  result: TItem[];
  pageSize: number;
  pageCount: number;
  rowCount: number;
};

export const cursorPagedResponseSchema = <TItemSchema extends z.ZodType>(itemSchema: TItemSchema) =>
  z.discriminatedUnion('hasMore', [
    z.object({
      items: z.array(itemSchema),
      cursor: z.string(),
      hasMore: z.literal(true),
    }),
    z.object({
      items: z.array(itemSchema),
      cursor: z.null(),
      hasMore: z.literal(false),
    }),
  ]);

export type CursorPagedResponse<TItem> =
  | {
      items: TItem[];
      cursor: string;
      hasMore: true;
    }
  | {
      items: TItem[];
      cursor: null;
      hasMore: false;
    };

export interface CursorRequestParams {
  limit: number;
  cursor?: string;
}

export const createEntityResponseSchema = z.object({
  id: z.string(),
});

export type CreateEntityResponse = z.infer<typeof createEntityResponseSchema>;

export const apiDate = <TType extends ZodType = ZodDate>(chainSchema?: (date: ZodDate) => TType) =>
  z.preprocess(
    (value) => (value === null ? null : new Date(value as string)),
    chainSchema?.(z.date()) ?? z.date()
  );
